import { ticketChannels, ticketPriorities, ticketStatuses } from '@gozoki/api-types'
import { getRouteApi } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useCallback } from 'react'
import { z } from 'zod'
import { type Period, periodDateRanges, periods } from '~/components/new/app-ui/period-select'

/**
 * État des filtres de la page Service client, **stocké dans l'URL** (`validateSearch` de la route) :
 * vues partageables, qui survivent au refresh et au back/forward. Globaux (barre d'onglets) :
 * boutiques + période, partagés entre onglets. Propres à la Boîte de réception : statut, priorité,
 * canal, agent, recherche texte. Le filtre « Sujet (IA) » est hors scope de ce lot.
 */

export const serviceClientSearchSchema = z.object({
  tab: z.enum(['dashboard', 'inbox', 'clients', 'reviews']).optional(),
  // Globaux (barre d'onglets).
  storeIds: z.array(z.number()).optional(),
  period: z.enum(periods).optional(),
  // Filtres de liste (Boîte de réception). Énumérations partagées = tuples `as const`.
  status: z.enum(ticketStatuses).optional(),
  priority: z.enum(ticketPriorities).optional(),
  channel: z.enum(ticketChannels).optional(),
  agentId: z.number().optional(),
  search: z.string().optional(),
})

export type ServiceClientSearch = z.infer<typeof serviceClientSearchSchema>

/** Période par défaut : « 7 derniers jours » (décision produit). */
export const DEFAULT_PERIOD: Period = '7-days'

/** Rappel des dates résolues du sélecteur de période (ex. « 15 – 21 sept. »), affiché à côté. */
export function formatPeriodRange(period: Period): string {
  const { start, end } = periodDateRanges[period]()
  const fmt = (iso: string) => DateTime.fromISO(iso).setLocale('fr').toFormat('d MMM')
  return `${fmt(start)} – ${fmt(end)}`
}

export const serviceClientRoute = getRouteApi('/_authenticated/service-client/')

/**
 * Lit/écrit les filtres dans l'URL. `setSearch` applique un patch partiel (une clé à `undefined`
 * efface le filtre). `period` résout toujours vers une valeur (défaut 7 j).
 */
export function useServiceClientFilters() {
  const search = serviceClientRoute.useSearch()
  const navigate = serviceClientRoute.useNavigate()
  // `setSearch` stable (navigate l'est) : sûr en dépendance d'un `useCallback`/`useEffect` consommateur.
  const setSearch = useCallback(
    (patch: Partial<ServiceClientSearch>) =>
      navigate({ search: (prev) => ({ ...prev, ...patch }) }),
    [navigate],
  )
  return { search, period: search.period ?? DEFAULT_PERIOD, setSearch }
}

/**
 * Query acceptée par `useInboxTickets` (tous les filtres optionnels). `isRead` n'est pas produit par
 * `toInboxQuery` (il ne vient pas d'un filtre d'URL) mais sert au compteur de non lus du badge.
 */
export type InboxQuery = Partial<ReturnType<typeof toInboxQuery>> & { isRead?: boolean }

/** Query envoyée au endpoint `service_client_tickets.index` à partir des filtres résolus. */
export function toInboxQuery(search: ServiceClientSearch, period: Period) {
  const { start, end } = periodDateRanges[period]()
  return {
    status: search.status,
    priority: search.priority,
    channel: search.channel,
    assignedAdminId: search.agentId,
    storeIds: search.storeIds && search.storeIds.length > 0 ? search.storeIds : undefined,
    dateFrom: start,
    dateTo: end,
    search: search.search?.trim() || undefined,
  }
}
